import { useEffect, useMemo } from 'react'

import { useQuery } from '@apollo/client'
import { useParams } from 'react-router-dom'

import {
  OrderPartialPaymentRequestsDocument,
  OrderPriceRange,
  OrderRemainingBalanceDocument,
  PartialPaymentRequest,
  PaymentInfo,
  PublicOrderPartialPaymentRequestsDocument,
  PublicOrderRemainingBalanceDocument,
} from '__generated__/atlas-types'

import { useUserAnalytics } from 'utils/sdks/userAnalytics'
import { relayConnectionToArray } from 'utils/transformations/graphql'

import { atlasClient } from 'graphql/client'
import { atlas } from 'graphql/tags'

import { TransformedPaymentRecords } from '../../types'
import { temporaryPendingPayments } from '../store'
import { useGetRatesQuery, usePaymentProvidersQuery } from './Payment.queries'
import { getCurrencyByCountry } from './helpers'

atlas`#graphql 
  query OrderPartialPaymentRequests($orderId: ID!) {
    orders(filters: {ids: [$orderId]}){
      edges {
        node {
          id
          paymentInfo {
            partialPaymentRequests {
              id
              amount
              name
              active
            }
          }
        }
      }
    }
  }
`

atlas`#graphql 
  query PublicOrderPartialPaymentRequests($uuid: String!) {
    public  {
      getOrderByInvoice(invoiceId: $uuid) {
        partialPaymentRequests {
          id
          amount
          name
          active
          currency
        }
      }
    }
  }
`

atlas`#graphql
  query OrderRemainingBalance($filters: OrderFilterInput!) {
    orders(filters: $filters) {
      edges {
        node {
          id
          paymentInfo {
            paidAmount
            remainingBalance
          }
        }
      }
    }
  }
`

atlas`#graphql
  query PublicOrderRemainingBalance($uuid: String!) {
    public {
      getOrderByInvoice(invoiceId: $uuid) {
        paymentInfo {
            paidAmount
            remainingBalance
          }
      }
    }
  }
`

type UseLocalCurrencyDataParams = {
  shouldUseLocalCurrencies: boolean
  retailerId?: string
  order?: {
    id: string
    currencyCode?: string
    retailer?: { id: string }
    brand: { id: string }
    billingCountry?: string
    destinationAccountId?: string
    billing?: {
      address: {
        country: string
      }
    }
  }
  externalPayment?: {
    brandId: string
    id: string
    paymentProvider?: { merchantId?: string | null } | null
    retailerName: string
    billingCountry?: string
    currency?: string
  }
}

export const useLocalCurrencyData = ({
  shouldUseLocalCurrencies,
  retailerId,
  order,
  externalPayment,
}: UseLocalCurrencyDataParams) => {
  const { trackEvent } = useUserAnalytics()
  const { uuid } = useParams<{ uuid?: string }>()
  const currencyCode = order?.currencyCode ?? externalPayment?.currency
  // graphene has billingCountry, atlas has billing.address.country
  const localCurrency = getCurrencyByCountry(
    (order?.billingCountry || order?.billing?.address?.country) ??
      externalPayment?.billingCountry,
  )

  const { data: providersData, loading: loadingProviders } =
    usePaymentProvidersQuery({
      // in the order obj from the pay tab the destination id comes from order.brand.id
      // while in the invoice page is found in destinationAccountId
      orderDestinationAccountId:
        order?.brand?.id ?? order?.destinationAccountId,
      retailerId,
      shouldUseLocalCurrencies,
      uuid,
    })

  const {
    data,
    loading: loadingRates,
    error: getRatesError,
  } = useGetRatesQuery({
    currencyCode,
    externalPayment,
    order,
    providersData,
    shouldUseLocalCurrencies,
  })

  const baseRateOffer = data?.rates?.RateOffers.find((ro) => ro.Rate === 1)
  const rateOffer = data?.rates?.RateOffers.find(
    (ro) => ro.Currency === localCurrency,
  )

  useEffect(() => {
    if (
      !shouldUseLocalCurrencies ||
      !localCurrency ||
      !rateOffer ||
      !baseRateOffer
    ) {
      return
    }
    trackEvent(
      baseRateOffer?.Currency !== currencyCode
        ? 'PAYMENTS - BASE CUR NOT EQUAL'
        : 'PAYMENTS - LOCAL CURRENCY ENABLED',
      {
        orderId: order?.id,
        currency: currencyCode,
        brandId: order?.brand.id ?? externalPayment?.brandId,
        retailerId: order?.retailer?.id ?? externalPayment?.retailerName,
        externalPaymentId: externalPayment?.id,
      },
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldUseLocalCurrencies, localCurrency, rateOffer, baseRateOffer])

  useEffect(() => {
    if (shouldUseLocalCurrencies && !localCurrency) {
      trackEvent('PAYMENTS - LOCAL PAYMENTS NA', {
        orderId: order?.id,
        currency: currencyCode,
        brandId: order?.brand.id ?? externalPayment?.brandId,
        retailerId: order?.retailer?.id ?? externalPayment?.retailerName,
        externalPaymentId: externalPayment?.id,
        billingCountry:
          order?.billingCountry ?? externalPayment?.billingCountry,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldUseLocalCurrencies, localCurrency])

  useEffect(() => {
    if (shouldUseLocalCurrencies && !!localCurrency && !rateOffer) {
      trackEvent('PAYMENTS - RATE OFFER NOT FOUND', {
        orderId: order?.id,
        currency: currencyCode,
        brandId: order?.brand.id ?? externalPayment?.brandId,
        retailerId: order?.retailer?.id ?? externalPayment?.retailerName,
        billingCountry:
          order?.billingCountry ?? externalPayment?.billingCountry,
        externalPaymentId: externalPayment?.id,
        localCurrency,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldUseLocalCurrencies, localCurrency, rateOffer])

  const retailerError =
    order && !retailerId
      ? new Error(
          'No retailer selected for the order. Assign a Retailer and try again',
        )
      : undefined
  return {
    baseRateOffer,
    rateOffer,
    localCurrency,
    loading: loadingRates || loadingProviders,
    error: getRatesError
      ? new Error(
          'Error fetching rates, please refresh the page and try again!',
        )
      : retailerError,
  }
}

export type PaymentOrderInfo = {
  id: string
  brand: {
    id?: string
  }
  retailer?: {
    id?: string
  }
  grandTotal?: {
    minPrice?: number
  }
  orderTotal?: any
  financials?: {
    dueTotal: OrderPriceRange
  }
}

export type OrderTotalAmount = {
  unpaidAmount: number
  loading: boolean
  error: Error | undefined
}

export const useOrderPartialPaymentRequests = (
  orderId?: string,
  uuid?: string,
) => {
  const isPublic = Boolean(uuid)

  const { data: partialPayments, loading: partialPaymentsLoading } = useQuery(
    OrderPartialPaymentRequestsDocument,
    {
      client: atlasClient,
      variables: { orderId: orderId || '' },
      skip: !orderId || isPublic,
    },
  )

  const { data: publicPartialPayments, loading: publicPartialPaymentsLoading } =
    useQuery(PublicOrderPartialPaymentRequestsDocument, {
      client: atlasClient,
      variables: { uuid: uuid || '' },
      skip: !uuid || !isPublic,
    })

  const partialPaymentRequest = useMemo<PartialPaymentRequest | undefined>(
    () =>
      isPublic
        ? publicPartialPayments?.public.getOrderByInvoice
            .partialPaymentRequests?.[0]
        : relayConnectionToArray(partialPayments?.orders)?.[0]?.paymentInfo
            ?.partialPaymentRequests?.[0],
    [partialPayments?.orders, publicPartialPayments?.public, isPublic],
  )

  return {
    partialPaymentRequest,
    loading: partialPaymentsLoading || publicPartialPaymentsLoading,
  }
}

const getTemporaryPaymentsTotal = () => {
  const temporaryPendingPayment = temporaryPendingPayments()

  return temporaryPendingPayment.reduce(
    (pendingTotalAmount: number, currPayment: TransformedPaymentRecords) =>
      pendingTotalAmount + (currPayment?.amount || 0),
    0,
  )
}

export const useOrderRemainingBalance = (orderId?: string, skip?: boolean) => {
  const { externalPaymentUUID, uuid } = useParams<{
    externalPaymentUUID?: string
    uuid?: string
  }>()
  const isPublic = Boolean(uuid)
  const isExternalPayment = Boolean(externalPaymentUUID)

  const {
    data: orderRemainingBalance,
    loading: orderRemainingBalanceLoading,
    error: orderRemainingBalanceError,
  } = useQuery(OrderRemainingBalanceDocument, {
    client: atlasClient,
    fetchPolicy: 'network-only',
    variables: {
      filters: {
        ids: [orderId as string],
      },
    },
    skip: skip || !orderId || isExternalPayment || isPublic,
  })

  const {
    data: publicOrderRemainingBalance,
    loading: publicOrderRemainingBalanceLoading,
    error: publicOrderRemainingBalanceError,
  } = useQuery(PublicOrderRemainingBalanceDocument, {
    client: atlasClient,
    fetchPolicy: 'network-only',
    variables: { uuid: uuid || '' },
    skip: skip || !isPublic || isExternalPayment || !uuid,
  })

  const paymentInfo: PaymentInfo = isPublic
    ? publicOrderRemainingBalance?.public?.getOrderByInvoice?.paymentInfo
    : relayConnectionToArray(orderRemainingBalance?.orders)?.[0]?.paymentInfo
  const remainingBalance: number = Number(paymentInfo?.remainingBalance || 0)

  let amountToRemoveFromTotal = 0

  if (remainingBalance) {
    // We need to take into account the amount from the temporaryPendingPayment because we
    // are not calculating anymore the amount in the frontend, and if we have a processing
    // payment that it has not been processed yet, we need to use the temporary payment
    const temporaryPaymentsTotal = getTemporaryPaymentsTotal()

    const usesAnonymousMutations = !!externalPaymentUUID || !!uuid
    // We are checking also for Anonymous case because we are still calculating the
    // amount there, this should be removed as soon as we have PaymentInfo public
    amountToRemoveFromTotal = !usesAnonymousMutations
      ? temporaryPaymentsTotal || 0
      : 0
  }

  // Fixme: we are deducting/agregating twice the amountToRemoveFromTotal value. We need
  // to find out a way to only do it once
  return {
    remainingBalance: remainingBalance - amountToRemoveFromTotal,
    loading: orderRemainingBalanceLoading || publicOrderRemainingBalanceLoading,
    error: orderRemainingBalanceError || publicOrderRemainingBalanceError,
  }
}
